import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageBody, EmptyState } from "@/components/ui-bits";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ReferenceDot } from "recharts";
import { Download } from "lucide-react";

export default function Telemetry() {
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [metric, setMetric] = useState("");
  const [minutes, setMinutes] = useState(60);
  const [data, setData] = useState([]);

  useEffect(() => { (async () => {
    const { data } = await api.get("/devices");
    setDevices(data);
    if (data[0]) setDeviceId(data[0].device_id);
  })(); }, []);

  useEffect(() => {
    if (!deviceId) return;
    (async () => {
      const params = { device_id: deviceId, minutes };
      if (metric) params.metric = metric;
      const { data } = await api.get("/telemetry", { params });
      setData(data);
    })();
    const t = setInterval(async () => {
      if (!deviceId) return;
      const params = { device_id: deviceId, minutes };
      if (metric) params.metric = metric;
      const { data } = await api.get("/telemetry", { params });
      setData(data);
    }, 8000);
    return () => clearInterval(t);
  }, [deviceId, metric, minutes]);

  const metrics = useMemo(() => Array.from(new Set(data.map(d => d.metric))), [data]);
  useEffect(() => { if (metrics.length && !metric) setMetric(metrics[0]); }, [metrics, metric]);

  const chart = useMemo(() => data
    .filter(d => !metric || d.metric === metric)
    .map(d => ({
      t: new Date(d.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
      ts: d.ts, v: d.value, anomaly: d.anomaly,
    })), [data, metric]);

  const exportCsv = async () => {
    const resp = await api.get("/telemetry/export", { params: { device_id: deviceId, minutes }, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `telemetry-${deviceId}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="telemetry-page">
      <PageHeader
        eyebrow="// LIVE STREAM"
        title="Telemetry"
        description="Real-time sensor readings with z-score anomaly markers."
        actions={
          <button onClick={exportCsv} data-testid="export-csv-btn" className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-900 text-sm font-semibold hover:bg-neutral-900 hover:text-white">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        }
      />
      <PageBody>
        {devices.length === 0 ? (
          <EmptyState title="No devices to stream" body="Register a device first." />
        ) : (
          <>
            <div className="flex flex-wrap gap-3 mb-6">
              <Select value={deviceId} onValueChange={setDeviceId}>
                <SelectTrigger data-testid="device-select" className="w-64"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {devices.map(d => <SelectItem key={d.device_id} value={d.device_id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={metric || (metrics[0] || "")} onValueChange={setMetric}>
                <SelectTrigger data-testid="metric-select" className="w-48"><SelectValue placeholder="Metric" /></SelectTrigger>
                <SelectContent>
                  {(metrics.length ? metrics : ["temperature","humidity","co2","vibration","pressure"]).map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={String(minutes)} onValueChange={(v) => setMinutes(parseInt(v))}>
                <SelectTrigger data-testid="window-select" className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[15, 60, 360, 1440].map(m => <SelectItem key={m} value={String(m)}>{m < 60 ? `${m}m` : `${m/60}h`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="border border-neutral-200 bg-white p-6">
              <div className="label-eyebrow text-neutral-500">// SERIES</div>
              <div className="font-display text-xl font-semibold mt-1 mb-4">
                {metric || "—"} <span className="text-neutral-400 font-mono text-sm">({chart.length} pts)</span>
              </div>
              <div className="h-[420px]">
                {chart.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-neutral-500 text-sm">No data in window</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chart}>
                      <CartesianGrid stroke="#E5E5E5" strokeDasharray="2 2" vertical={false} />
                      <XAxis dataKey="t" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#A3A3A3" />
                      <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#A3A3A3" />
                      <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #0A0A0A", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                      <Line type="monotone" dataKey="v" stroke="#002FA7" strokeWidth={2} dot={false} />
                      {chart.map((p, i) => p.anomaly && (
                        <ReferenceDot key={i} x={p.t} y={p.v} r={4} fill="#FF3B30" stroke="none" />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="mt-6 border border-neutral-200 bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr className="text-left label-eyebrow text-neutral-500">
                    <th className="px-4 py-3">Time</th>
                    <th className="px-4 py-3">Metric</th>
                    <th className="px-4 py-3">Value</th>
                    <th className="px-4 py-3">Unit</th>
                    <th className="px-4 py-3">Anomaly</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data].reverse().slice(0, 50).map(r => (
                    <tr key={r.reading_id} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="px-4 py-2 font-mono text-xs">{new Date(r.ts).toLocaleString()}</td>
                      <td className="px-4 py-2 font-mono text-xs">{r.metric}</td>
                      <td className="px-4 py-2 font-mono">{r.value}</td>
                      <td className="px-4 py-2 font-mono text-xs text-neutral-500">{r.unit || "—"}</td>
                      <td className="px-4 py-2">{r.anomaly ? <span className="text-[#FF3B30] font-mono text-xs">YES</span> : <span className="text-neutral-400 font-mono text-xs">no</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </PageBody>
    </div>
  );
}
